// @ts-nocheck
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { PostTemplate, TemplateField } from './wallPostTemplates';
import { X, Upload, Loader2, ArrowLeft, Image as ImageIcon, Video } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getSessionUser } from '@/lib/authSession';

interface TemplatePostFormProps {
  template: PostTemplate;
  onCancel: () => void;
  onSubmit: (data: {
    template_id: string;
    template_data: Record<string, string>;
    media_urls: string[];
  }) => void;
  isSubmitting: boolean;
}

interface MediaUpload {
  fieldId: string;
  file: File;
  previewUrl: string;
  type: 'image' | 'video';
}

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

const TemplatePostForm: React.FC<TemplatePostFormProps> = ({
  template,
  onCancel,
  onSubmit,
  isSubmitting,
}) => {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [mediaUploads, setMediaUploads] = useState<MediaUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFieldChange = (fieldId: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleFileSelect = (field: TemplateField, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = field.type === 'video';
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;

    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: `Maximum size is ${isVideo ? '50MB' : '10MB'}`,
        variant: 'destructive',
      });
      return;
    }

    // Remove existing upload for this field
    setMediaUploads((prev) => {
      const existing = prev.find((m) => m.fieldId === field.id);
      if (existing) {
        URL.revokeObjectURL(existing.previewUrl);
      }
      return prev.filter((m) => m.fieldId !== field.id);
    });

    const upload: MediaUpload = {
      fieldId: field.id,
      file,
      previewUrl: URL.createObjectURL(file),
      type: isVideo ? 'video' : 'image',
    };

    setMediaUploads((prev) => [...prev, upload]);

    if (fileInputRefs.current[field.id]) {
      fileInputRefs.current[field.id]!.value = '';
    }
  };

  const removeMedia = (fieldId: string) => {
    setMediaUploads((prev) => {
      const upload = prev.find((m) => m.fieldId === fieldId);
      if (upload) {
        URL.revokeObjectURL(upload.previewUrl);
      }
      return prev.filter((m) => m.fieldId !== fieldId);
    });
  };

  const uploadMedia = async (): Promise<string[]> => {
    const uploadedUrls: string[] = [];

    for (const upload of mediaUploads) {
      const { data: { user } } = await getSessionUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = upload.file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('wall-media')
        .upload(fileName, upload.file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('wall-media')
        .getPublicUrl(fileName);

      uploadedUrls.push(publicUrl);
      // Store URL in field values too for template_data
      setFieldValues((prev) => ({ ...prev, [upload.fieldId]: publicUrl }));
    }

    return uploadedUrls;
  };

  const handleSubmit = async () => {
    // Validate required fields
    const missingFields = template.fields.filter((field) => {
      if (!field.required) return false;
      if (field.type === 'image' || field.type === 'video') {
        return !mediaUploads.find((m) => m.fieldId === field.id);
      }
      if (field.type === 'poll_options') {
        const validOptions = pollOptions.filter(o => o.trim());
        return validOptions.length < 2;
      }
      return !fieldValues[field.id]?.trim();
    });

    if (missingFields.length > 0) {
      toast({
        title: 'Missing required fields',
        description: `Please fill in: ${missingFields.map((f) => f.label).join(', ')}`,
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsUploading(true);

      // Upload media files
      const mediaUrls = await uploadMedia();

      // Build template_data with all values including uploaded URLs
      const finalTemplateData: Record<string, string | string[]> = { ...fieldValues };
      mediaUploads.forEach((upload, index) => {
        if (mediaUrls[index]) {
          finalTemplateData[upload.fieldId] = mediaUrls[index];
        }
      });
      
      // Add poll options if present
      const pollField = template.fields.find(f => f.type === 'poll_options');
      if (pollField) {
        finalTemplateData.poll_options = pollOptions.filter(o => o.trim());
      }

      onSubmit({
        template_id: template.id,
        template_data: finalTemplateData as Record<string, string>,
        media_urls: mediaUrls,
      });
    } catch (error) {
      console.error('Error uploading media:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload media. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const isLoading = isSubmitting || isUploading;

  const renderField = (field: TemplateField) => {
    const upload = mediaUploads.find((m) => m.fieldId === field.id);

    if (field.type === 'image' || field.type === 'video') {
      return (
        <div key={field.id} className="space-y-2">
          <Label className="flex items-center gap-2">
            <span>{field.emoji}</span>
            <span>{field.label}</span>
            {field.required && <span className="text-destructive">*</span>}
          </Label>
          
          {upload ? (
            <div className="relative">
              {upload.type === 'image' ? (
                <img
                  src={upload.previewUrl}
                  alt="Preview"
                  className="w-full h-40 object-cover rounded-lg"
                />
              ) : (
                <video
                  src={upload.previewUrl}
                  className="w-full h-40 object-cover rounded-lg"
                  controls
                />
              )}
              <button
                type="button"
                onClick={() => removeMedia(field.id)}
                className="absolute top-2 right-2 p-1 bg-black/50 rounded-full"
                disabled={isLoading}
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
          ) : (
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => fileInputRefs.current[field.id]?.click()}
            >
              <input
                ref={(el) => (fileInputRefs.current[field.id] = el)}
                type="file"
                accept={field.type === 'video' ? 'video/mp4,video/quicktime' : 'image/jpeg,image/png,image/gif,image/webp'}
                onChange={(e) => handleFileSelect(field, e)}
                className="hidden"
                disabled={isLoading}
              />
              {field.type === 'video' ? (
                <Video className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              ) : (
                <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              )}
              <p className="text-sm text-muted-foreground">{field.placeholder}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Max {field.type === 'video' ? '50MB' : '10MB'}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.id} className="space-y-2">
          <Label className="flex items-center gap-2">
            <span>{field.emoji}</span>
            <span>{field.label}</span>
            {field.required && <span className="text-destructive">*</span>}
          </Label>
          <Textarea
            value={fieldValues[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            disabled={isLoading}
            rows={3}
          />
        </div>
      );
    }

    if (field.type === 'poll_options') {
      return (
        <div key={field.id} className="space-y-2">
          <Label className="flex items-center gap-2">
            <span>{field.emoji}</span>
            <span>{field.label}</span>
            {field.required && <span className="text-destructive">*</span>}
          </Label>
          <div className="space-y-2">
            {pollOptions.map((option, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...pollOptions];
                    newOptions[index] = e.target.value;
                    setPollOptions(newOptions);
                  }}
                  placeholder={`Option ${index + 1}`}
                  disabled={isLoading}
                />
                {pollOptions.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== index))}
                    disabled={isLoading}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {pollOptions.length < 4 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPollOptions([...pollOptions, ''])}
                disabled={isLoading}
              >
                + Add Option
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={field.id} className="space-y-2">
        <Label className="flex items-center gap-2">
          <span>{field.emoji}</span>
          <span>{field.label}</span>
          {field.required && <span className="text-destructive">*</span>}
        </Label>
        <Input
          value={fieldValues[field.id] || ''}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          placeholder={field.placeholder}
          disabled={isLoading}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 pb-3 border-b">
        <button
          type="button"
          onClick={onCancel}
          className="p-1 hover:bg-accent rounded-full transition-colors"
          disabled={isLoading}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg">{template.displayEmojis.join(' ')}</span>
            <span className="font-semibold">{template.name}</span>
          </div>
          <p className="text-sm text-muted-foreground">{template.description}</p>
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-4">
        {template.fields.map(renderField)}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isLoading}
        >
          Back to freeform
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          size="sm"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {isUploading ? 'Uploading...' : 'Posting...'}
            </>
          ) : (
            'Post'
          )}
        </Button>
      </div>
    </div>
  );
};

export default TemplatePostForm;